import { baseApi } from 'lib/rtkQuery/baseApi';
import { QueryTags } from 'lib/rtkQuery/constants';
import { QueryResponse } from 'lib/rtkQuery/types';
import {
  TDeleteHotelImageParams,
  TGetHotelBookingReportParams,
  TGetHotelBookingReportResponse,
  TGetHotelListParams,
  TGetHotelListResponse,
  TGetHotelParams,
  TGetHotelRoomDetailParams,
  TGetHotelRoomParams,
  TGetReferenceParams,
  THotelImage,
  THotelInfo,
  THotelRoomDetail,
  THotelRoomListItem,
  TMapPeakToPropertyPayload,
  TRegion,
  TSearchRegionParams,
  TUpsertHotelPayload,
  TUpsertHotelRoomPayload,
} from './types';

export const regionApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    SearchRegion: builder.query<QueryResponse<TRegion[]>, TSearchRegionParams>({
      query: (params: TSearchRegionParams) => ({
        url: `/ocs/region/search`,
        method: 'GET',
        params,
      }),
    }),
  }),
});

export const hotelReferenceApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    GetReference: builder.query<QueryResponse<TRegion[]>, TGetReferenceParams>({
      query: (params: TGetReferenceParams) => ({
        url: `/reference/get-reference`,
        method: 'GET',
        params,
      }),
    }),
  }),
});

export const hotelBookingReportApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    GetHotelBookingReport: builder.query<
      QueryResponse<TGetHotelBookingReportResponse>,
      TGetHotelBookingReportParams
    >({
      query: (params: TGetHotelBookingReportParams) => ({
        url: `/ocs/report`,
        method: 'GET',
        params,
      }),
    }),
  }),
});

const hotelApiEndpoint = '/ocs/property/';
const hotelRoomApiEndpoint = '/ocs/room/';

export const hotelApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      QueryTags.HotelList,
      QueryTags.HotelDetail,
      QueryTags.HotelDetailGallery,
    ],
  })
  .injectEndpoints({
    endpoints: builder => ({
      GetHotelList: builder.query<
        QueryResponse<TGetHotelListResponse>,
        TGetHotelListParams
      >({
        query: params => ({
          url: `${hotelApiEndpoint}get-list`,
          method: 'GET',
          params,
        }),
        providesTags: [QueryTags.HotelList],
      }),

      GetHotel: builder.query<QueryResponse<THotelInfo>, TGetHotelParams>({
        query: params => ({
          url: `${hotelApiEndpoint}get`,
          method: 'GET',
          params,
        }),
        providesTags: [QueryTags.HotelDetail],
      }),

      UpsertHotel: builder.mutation<QueryResponse<string>, TUpsertHotelPayload>(
        {
          query: payload => ({
            url: `${hotelApiEndpoint}upsert`,
            method: 'POST',
            data: payload,
          }),
          invalidatesTags: (_, error) =>
            !!error ? [] : [QueryTags.HotelList, QueryTags.HotelDetail],
        }
      ),

      DeleteHotel: builder.mutation<
        QueryResponse<string>,
        Pick<TGetHotelParams, 'propertyId'>
      >({
        query: params => ({
          url: `${hotelApiEndpoint}delete`,
          method: 'DELETE',
          params,
        }),
        invalidatesTags: (_, error) => (!!error ? [] : [QueryTags.HotelList]),
      }),

      GetHotelImages: builder.query<
        QueryResponse<THotelImage[]>,
        Pick<TGetHotelParams, 'propertyId'>
      >({
        query: params => ({
          url: `${hotelApiEndpoint}get-image`,
          method: 'GET',
          params,
        }),
        providesTags: [QueryTags.HotelDetailGallery],
      }),

      UploadHotelImages: builder.mutation<QueryResponse<string>, FormData>({
        query: (data: FormData) => ({
          url: `${hotelApiEndpoint}upload-image`,
          method: 'POST',
          data,
        }),
        invalidatesTags: (_, error) =>
          !!error ? [] : [QueryTags.HotelDetailGallery],
      }),

      DeleteHotelImage: builder.mutation<
        QueryResponse<string>,
        TDeleteHotelImageParams
      >({
        query: (params: TDeleteHotelImageParams) => ({
          url: `${hotelApiEndpoint}delete-image`,
          method: 'DELETE',
          params,
        }),
        invalidatesTags: (_, error) =>
          !!error ? [] : [QueryTags.HotelDetailGallery],
      }),

      MapPeakToHotel: builder.mutation<
        QueryResponse<string>,
        TMapPeakToPropertyPayload
      >({
        query: ({ params, data }: TMapPeakToPropertyPayload) => ({
          url: `${hotelApiEndpoint}assign-peaks-to-all-rooms`,
          method: 'POST',
          data,
          params,
        }),
      }),
    }),
  });

export const hotelRoomApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [QueryTags.HotelRoomList, QueryTags.HotelRoomDetail],
  })
  .injectEndpoints({
    endpoints: builder => ({
      GetHotelRooms: builder.query<
        QueryResponse<THotelRoomListItem[]>,
        TGetHotelRoomParams
      >({
        query: params => ({
          url: `${hotelRoomApiEndpoint}get-list`,
          method: 'GET',
          params,
        }),
        providesTags: [QueryTags.HotelRoomList],
      }),

      GetHotelRoomDetail: builder.query<
        QueryResponse<THotelRoomDetail>,
        TGetHotelRoomDetailParams
      >({
        query: params => ({
          url: `${hotelRoomApiEndpoint}get`,
          method: 'GET',
          params,
        }),
        providesTags: [QueryTags.HotelRoomDetail],
      }),

      UpsertHotelRoom: builder.mutation<
        QueryResponse<string>,
        TUpsertHotelRoomPayload
      >({
        query: payload => ({
          url: `${hotelRoomApiEndpoint}upsert`,
          method: 'POST',
          data: payload,
        }),
        invalidatesTags: (_, error) =>
          !!error ? [] : [QueryTags.HotelRoomList, QueryTags.HotelRoomDetail],
      }),

      DeleteHotelRoom: builder.mutation<
        QueryResponse<string>,
        Pick<TGetHotelRoomDetailParams, 'roomId'>
      >({
        query: params => ({
          url: `${hotelRoomApiEndpoint}delete`,
          method: 'DELETE',
          params,
        }),
        invalidatesTags: (_, error) =>
          !!error ? [] : [QueryTags.HotelRoomList],
      }),

      GetHotelRoomImages: builder.query<
        QueryResponse<THotelImage[]>,
        Pick<TGetHotelRoomDetailParams, 'roomId'>
      >({
        query: params => ({
          url: `${hotelRoomApiEndpoint}get-image`,
          method: 'GET',
          params,
        }),
        providesTags: [QueryTags.HotelRoomDetailGallery],
      }),

      UploadHotelRoomImages: builder.mutation<QueryResponse<string>, FormData>({
        query: (data: FormData) => ({
          url: `${hotelRoomApiEndpoint}upload-image`,
          method: 'POST',
          data,
        }),
        invalidatesTags: (_, error) =>
          !!error ? [] : [QueryTags.HotelRoomDetailGallery],
      }),

      DeleteHotelRoomImage: builder.mutation<
        QueryResponse<string>,
        TDeleteHotelImageParams
      >({
        query: (params: TDeleteHotelImageParams) => ({
          url: `${hotelRoomApiEndpoint}delete-image`,
          method: 'DELETE',
          params,
        }),
        invalidatesTags: (_, error) =>
          !!error ? [] : [QueryTags.HotelRoomDetailGallery],
      }),
    }),
  });

export const { useSearchRegionQuery } = regionApi;
export const { useGetReferenceQuery } = hotelReferenceApi;
export const { useGetHotelBookingReportQuery } = hotelBookingReportApi;
export const {
  useUpsertHotelMutation,
  useGetHotelQuery,
  useGetHotelListQuery,
  useDeleteHotelMutation,
  useGetHotelImagesQuery,
  useUploadHotelImagesMutation,
  useDeleteHotelImageMutation,
  useMapPeakToHotelMutation,
} = hotelApi;

export const {
  useGetHotelRoomsQuery,
  useUpsertHotelRoomMutation,
  useGetHotelRoomDetailQuery,
  useDeleteHotelRoomMutation,
  useGetHotelRoomImagesQuery,
  useUploadHotelRoomImagesMutation,
  useDeleteHotelRoomImageMutation,
} = hotelRoomApi;
